import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FaBullhorn, FaChevronLeft } from 'react-icons/fa';
import { showAdminError, showAdminSuccess } from '../Common/adminResponseAlert';
import Navbar from '../../../../ReusableBlocks/Navbar';
import * as PATH from '../../../../CentralisedLink';
import { fetchAlert } from '../../../../Redux/Dashboard/Components/AlertAction';
import {
  deleteAdminAlertAct,
  fetchAdminAlertAct,
  saveAdminAlertAct,
} from '../../../../Redux/Dashboard/External/AdminAlerts/Actions/AdminAlertActions';
import DeleteAlertModal from './Components/DeleteAlertModal';

const AlertsPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const lastSyncedMessageRef = useRef(undefined);

  const {
    message,
    loading,
    saving,
    deleting,
    error,
    deleteError,
  } = useSelector((state) => state.AdminAlert || {
    message: null,
    loading: false,
    saving: false,
    deleting: false,
    error: null,
    deleteError: null,
  });

  const [draftMessage, setDraftMessage] = useState('');
  const [validationError, setValidationError] = useState('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchAdminAlertAct());
  }, [dispatch]);

  useEffect(() => {
    if (!loading && !error && lastSyncedMessageRef.current !== message) {
      setDraftMessage(message ?? '');
      lastSyncedMessageRef.current = message;
      setValidationError('');
    }
  }, [loading, error, message]);

  const alertRecordExists = message !== null;
  const hasVisibleAlert = typeof message === 'string' && message.trim().length > 0;
  const currentMessageText = message ?? '';
  const trimmedDraft = draftMessage.trim();
  const hasChanges = trimmedDraft !== currentMessageText.trim();
  const isBusy = loading || saving || deleting;
  const canSave = trimmedDraft.length > 0 && hasChanges && !isBusy;
  const showPreview = trimmedDraft.length > 0;

  const handleRetry = () => {
    dispatch(fetchAdminAlertAct());
  };

  const handleDraftChange = (event) => {
    setDraftMessage(event.target.value);
    if (validationError) {
      setValidationError('');
    }
  };

  const handleSave = async () => {
    if (isBusy) return;

    if (!trimmedDraft) {
      setValidationError('Enter an alert message before saving.');
      return;
    }

    if (!hasChanges) return;

    try {
      await dispatch(saveAdminAlertAct(trimmedDraft));
      lastSyncedMessageRef.current = trimmedDraft;
      setDraftMessage(trimmedDraft);
      setValidationError('');
      showAdminSuccess('Alert saved successfully.');
      await dispatch(fetchAlert());
    } catch (saveError) {
      showAdminError('Unable to save the alert', saveError?.message || 'Please try again.');
    }
  };

  const handleOpenDeleteModal = () => {
    if (!alertRecordExists || isBusy) return;
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    if (deleting) return;
    setIsDeleteModalOpen(false);
  };

  const handleConfirmDelete = async () => {
    if (deleting) return;

    try {
      const result = await dispatch(deleteAdminAlertAct());
      lastSyncedMessageRef.current = null;
      setDraftMessage('');
      setValidationError('');
      setIsDeleteModalOpen(false);

      if (result?.alreadyDeleted) {
        showAdminSuccess('Alert removed.');
      } else {
        showAdminSuccess('Alert deleted successfully.');
      }

      await dispatch(fetchAlert());
    } catch (deleteErr) {
      showAdminError('Unable to delete the alert', deleteErr?.message || 'Please try again.');
    }
  };

  const renderPanelContent = () => {
    if (loading) {
      return (
        <div className="admin-alerts-panel__loading">
          Loading alert...
        </div>
      );
    }

    if (error) {
      return (
        <div className="admin-alerts-panel__error">
          <p>{error}</p>
          <button
            type="button"
            className="admin-alerts-btn admin-alerts-btn--primary"
            onClick={handleRetry}
          >
            Retry
          </button>
        </div>
      );
    }

    return (
      <div className="admin-alerts-form">
        <div className="admin-alerts-form__header">
          <h2 className="admin-alerts-form__title">Global Alert</h2>
          <span
            className={`admin-alerts-status ${
              hasVisibleAlert ? 'admin-alerts-status--active' : 'admin-alerts-status--inactive'
            }`}
          >
            {hasVisibleAlert ? 'Active' : 'No active alert'}
          </span>
        </div>

        {hasVisibleAlert && (
          <p className="admin-alerts-form__description">
            This message is displayed across Tech Central.
          </p>
        )}

        {!alertRecordExists && (
          <p className="admin-alerts-form__description">
            Enter a message below to create one.
          </p>
        )}

        {alertRecordExists && !hasVisibleAlert && (
          <p className="admin-alerts-form__description">
            Update the message below or delete the stored alert.
          </p>
        )}

        <label className="admin-alerts-form__label" htmlFor="admin-alert-message">
          Message
        </label>
        <textarea
          id="admin-alert-message"
          className={`admin-alerts-textarea${validationError ? ' admin-alerts-textarea--error' : ''}`}
          value={draftMessage}
          onChange={handleDraftChange}
          placeholder="Enter the message you want to display to Tech Central users..."
          disabled={isBusy}
          rows={5}
        />
        {validationError && (
          <p className="admin-alerts-field-error">{validationError}</p>
        )}
        <p className="admin-alerts-char-count">
          {draftMessage.length} characters
        </p>

        {showPreview && (
          <div className="admin-alerts-preview">
            <span className="admin-alerts-preview__label">Preview</span>
            <div className="admin-alerts-preview__strip">
              {trimmedDraft}
            </div>
          </div>
        )}

        <div className="admin-alerts-form__actions">
          {alertRecordExists && (
            <button
              type="button"
              className="admin-alerts-btn admin-alerts-btn--destructive"
              onClick={handleOpenDeleteModal}
              disabled={isBusy}
            >
              Delete Alert
            </button>
          )}
          <button
            type="button"
            className="admin-alerts-btn admin-alerts-btn--primary"
            onClick={handleSave}
            disabled={!canSave}
          >
            {saving ? 'Saving...' : 'Save Alert'}
          </button>
        </div>
      </div>
    );
  };

  return (
    <>
      <Navbar activeTab="ADMIN" />
      <main className="admin-alerts-page">
        <div className="container">
          <section className="admin-alerts-page__intro">
            <div className="admin-alerts-page__title-row">
              <button
                type="button"
                className="admin-alerts-page__back-button"
                onClick={() => navigate(PATH.ADMIN_PATH)}
                aria-label="Back to Admin"
              >
                <FaChevronLeft />
              </button>
              <div className="admin-alerts-page__title-icon">
                <FaBullhorn />
              </div>
              <div>
                <h1 className="admin-alerts-page__title">Alerts</h1>
                <p className="admin-alerts-page__subtitle">
                  Manage the global alert shown across Tech Central.
                </p>
              </div>
            </div>
          </section>

          <section className="admin-alerts-panel">
            {renderPanelContent()}
          </section>
        </div>
      </main>

      <DeleteAlertModal
        isOpen={isDeleteModalOpen}
        currentMessage={message ?? ''}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        isDeleting={deleting}
        error={deleteError}
      />
    </>
  );
};

export default AlertsPage;
